import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as itemizationApi from '../services/itemizationApi.ts';
import { api } from '../services/api.ts';
import './ItemizationPanel.css';

interface Category {
  id: number;
  name: string;
  icon: string;
  icon_color: string;
}

interface Props {
  transactionId: number;
  transactionAmount: number;
}

const ItemizationPanel: React.FC<Props> = ({ transactionId, transactionAmount }) => {
  const [items, setItems] = useState<itemizationApi.TransactionItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [converters, setConverters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add item form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newCategoryId, setNewCategoryId] = useState<string>('');
  const [addingSaving, setAddingSaving] = useState(false);

  // Import
  const [sourceType, setSourceType] = useState('standard_csv');
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadItems = useCallback(async () => {
    try {
      const data = await itemizationApi.getItems(transactionId);
      setItems(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao carregar itens');
    }
  }, [transactionId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [itemsData, catsData, convsData] = await Promise.all([
          itemizationApi.getItems(transactionId),
          api.get<Category[]>('/api/v1/categories').then(r => r.data),
          itemizationApi.getConverters(),
        ]);
        setItems(itemsData);
        setCategories(catsData);
        setConverters(convsData);
        if (convsData.length > 0) setSourceType(convsData[0]);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [transactionId]);

  const totalItems = items.reduce((sum, it) => sum + it.amount, 0);
  const hasMismatch = items.length > 0 && Math.abs(totalItems - transactionAmount) > 0.01;
  const warnings = items.filter(it => it.status === 'warning');

  const handleDeleteItem = async (itemId: number) => {
    try {
      await itemizationApi.deleteItem(transactionId, itemId);
      setItems(prev => prev.filter(it => it.id !== itemId));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao excluir item');
    }
  };

  const handleCategoryChange = async (itemId: number, categoryId: string) => {
    try {
      const item = items.find(it => it.id === itemId);
      if (!item) return;
      const updated = await itemizationApi.updateItem(transactionId, itemId, {
        description: item.description,
        amount: item.amount,
        category_id: categoryId ? parseInt(categoryId) : null,
      });
      setItems(prev => prev.map(it => it.id === itemId ? updated : it));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao atualizar item');
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingSaving(true);
    setError('');
    try {
      const amt = parseFloat(newAmount.replace(',', '.'));
      if (!newDesc || isNaN(amt) || amt <= 0) {
        setError('Preencha descrição e valor válido');
        return;
      }
      const item = await itemizationApi.createItem(transactionId, {
        description: newDesc,
        amount: amt,
        category_id: newCategoryId ? parseInt(newCategoryId) : null,
      });
      setItems(prev => [...prev, item]);
      setNewDesc('');
      setNewAmount('');
      setNewCategoryId('');
      setShowAddForm(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao criar item');
    } finally {
      setAddingSaving(false);
    }
  };

  const handleFileImport = async (file: File) => {
    setImporting(true);
    setImportResult('');
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_type', sourceType);
      const result = await itemizationApi.importItems(transactionId, formData);
      setImportResult(`${result.confirmed} confirmados, ${result.warnings} pendentes`);
      await loadItems();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileImport(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileImport(file);
    e.target.value = '';
  };

  if (loading) return <div className="panel-loading">Carregando itens...</div>;

  return (
    <div className="itemization-panel">
      {/* Summary */}
      <div className="itemization-summary">
        <div className="summary-stat">
          <span className="stat-value">{items.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{items.filter(it => it.status === 'confirmed').length}</span>
          <span className="stat-label">Confirmados</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{warnings.length}</span>
          <span className="stat-label">Pendentes</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">
            {totalItems.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </span>
          <span className="stat-label">Soma</span>
        </div>
      </div>

      {hasMismatch && (
        <div className="summary-warning">
          ⚠️ Soma dos itens difere do valor da transação (
          {transactionAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})
        </div>
      )}

      {error && <div className="panel-error">{error}</div>}

      {/* Items list */}
      {items.length === 0 ? (
        <div className="empty-items">Nenhum item adicionado.</div>
      ) : (
        <div className="items-list">
          {items.map(item => (
            <div key={item.id} className={`item-row ${item.status}`}>
              <div className="item-status-dot" />
              <span className="item-desc">{item.description}</span>
              <span className="item-amount">
                {item.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
              {item.status === 'warning' ? (
                <select
                  className="item-category-select"
                  value={item.category_id ?? ''}
                  onChange={e => handleCategoryChange(item.id, e.target.value)}
                >
                  <option value="">Sem categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              ) : (
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {item.category ? `${item.category.icon} ${item.category.name}` : '—'}
                </span>
              )}
              <button className="item-delete-btn" onClick={() => handleDeleteItem(item.id)} title="Excluir item">✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Add item manually */}
      <div className="add-item-section">
        <h5>Adicionar item manualmente</h5>
        {!showAddForm ? (
          <button className="btn-secondary" onClick={() => setShowAddForm(true)}>+ Novo item</button>
        ) : (
          <form onSubmit={handleAddItem}>
            <div className="add-item-form">
              <div className="form-group add-item-full-row">
                <label>Descrição</label>
                <input
                  type="text"
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="Ex: Netflix, Mercado..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Valor</label>
                <input
                  type="text"
                  value={newAmount}
                  onChange={e => setNewAmount(e.target.value)}
                  placeholder="0,00"
                  required
                />
              </div>
              <div className="form-group">
                <label>Categoria</label>
                <select value={newCategoryId} onChange={e => setNewCategoryId(e.target.value)}>
                  <option value="">Sem categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="add-item-actions">
              <button type="submit" className="btn-primary" disabled={addingSaving}>
                {addingSaving ? 'Salvando...' : 'Adicionar'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowAddForm(false)}>Cancelar</button>
            </div>
          </form>
        )}
      </div>

      {/* Import file */}
      <div className="import-section">
        <h5>Importar arquivo</h5>
        <div className="import-controls">
          <select value={sourceType} onChange={e => setSourceType(e.target.value)}>
            {converters.map(c => (
              <option key={c} value={c}>{itemizationApi.CONVERTER_LABELS[c] ?? c}</option>
            ))}
          </select>
          <div
            className={`import-drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv,.xlsx" />
            {importing ? 'Importando...' : 'Arraste o arquivo aqui ou clique para selecionar'}
          </div>
          <div className="import-row-actions">
            <button className="btn-secondary" onClick={itemizationApi.downloadCSVTemplate}>
              ⬇ Baixar template CSV
            </button>
            {importResult && <span className="import-result">✓ {importResult}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemizationPanel;
